/** @jsx jsx */
import { jsx } from '@emotion/react';

import * as React from 'react';

import Retracked from 'js/lib/retracked';

import { SelectOption, SilentSelectField } from '@coursera/cds-core';
import { useTracker } from '@coursera/event-pulse/react';

import type Track from 'bundles/interactive-transcript/models/Track';
import type TrackList from 'bundles/interactive-transcript/models/TrackList';
import StorageUtils from 'bundles/interactive-transcript/utils/StorageUtils';
import { TrackingNamespace } from 'bundles/interactive-transcript/utils/eventing';

import _t from 'i18n!nls/interactive-transcript';

type Props = {
  tracks: TrackList;
  selectedTrack: Track;
  onTrackSelect?: (track: Track) => void;
  courseId?: string;
  itemId?: string;
};

type SelectParams = {
  label: string;
  languageCode: string;
};

const TrackChooser = ({ tracks, selectedTrack, onTrackSelect, courseId, itemId }: Props) => {
  const trackV3 = useTracker();

  const handleOpen = () => {
    trackV3('click_button', {
      button: {
        name: 'video_transcript_open',
      },
    });
  };

  const handleSelect = (
    e: React.ChangeEvent<{
      name?: string | undefined;
      value: unknown;
    }>
  ) => {
    const languageCode = e.target.value;
    const previousLanguage = selectedTrack.languageCode;
    const initialTrackingData = {
      itemId,
      courseId,
      previousLanguage,
    };

    // the first select option is not a language so we set the new language tracked to undefined
    if (languageCode === '-1') {
      const deselectedTrackingData = {
        ...initialTrackingData,
        newLanguage: undefined,
      };

      Retracked.trackComponent(TrackingNamespace, deselectedTrackingData, 'track_chooser', 'deselected');
    } else {
      const selectedTrackingData = {
        ...initialTrackingData,
        newLanguage: languageCode,
      };

      Retracked.trackComponent(TrackingNamespace, selectedTrackingData, 'track_chooser', 'selected');
    }

    const track = tracks.getFromLanguageCode(languageCode);

    StorageUtils.set('language-code', languageCode);
    trackV3('click_button', {
      button: {
        name: 'video_transcript_switch',
      },
      videoPlayer: {
        transcriptLanguage: typeof languageCode === 'string' ? languageCode : '',
        videoId: '',
      },
    });
    if (onTrackSelect) {
      onTrackSelect(track);
    }
  };
  return (
    <SilentSelectField
      data-testid="track-chooser-select"
      value={selectedTrack?.languageCode}
      onChange={handleSelect}
      onOpen={handleOpen}
      label={_t('Transcript language')}
      labelPlacement="integrated"
    >
      {tracks.toArray().map(({ label, languageCode }: SelectParams) => {
        return (
          <SelectOption key={`track-${languageCode}`} value={languageCode} aria-label={`${_t('Language')}, ${label}`}>
            {label}
          </SelectOption>
        );
      })}
    </SilentSelectField>
  );
};

export default TrackChooser;
